import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  ArrowLeft,
  BadgeCheck,
  Check,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  Send,
  Trash2,
  User,
  Wallet,
} from "lucide-react";

import { AdminCard, ListState, StatusPill } from "@/components/admin/primitives";
import { PAYMENT_METHOD_LABEL } from "@montebox/shared/payments";

import { PreviewBadge } from "@/components/admin/config/MercadoPagoSettings";
import { ItemPreview } from "@/components/ItemPreview";
import { OrderPdfButton } from "@/components/OrderPdfButton";
import { PreviewStage } from "@/components/PreviewStage";
import { UnitGridView } from "@/components/UnitGridView";
import { formatDateTime } from "@/lib/dates";
import { countLabel, formatBRL, padOrderNumber } from "@/lib/format";
import {
  ORDER_STATUSES,
  isEditableStatus,
  storeChatUrl,
  storeNoticeUrl,
  type OrderStatus,
  type StoredOrder,
} from "@/lib/orders";
import {
  useDeleteMyOrder,
  useDuplicateMyOrder,
  useMyOrder,
  useSendMyOrder,
} from "@/lib/queries";
import { useStoreSettings } from "@/lib/settings";
import { cn } from "@/lib/utils";

/**
 * Pedido aberto (cliente).
 *
 * A mesma informacao do painel, sem nada de gestao: o cliente ve o que montou,
 * em que etapa o pedido esta e o que falta ele fazer — enviar, ou aprovar.
 */
export const Route = createFileRoute("/minha-conta/_conta/pedidos/$pedidoId")({
  head: () => ({ meta: [{ title: "Meu pedido — MonteBox" }] }),
  component: ClientePedido,
});

/** Posicao de cada etapa na linha do tempo. */
const rank = (status: OrderStatus): number => ORDER_STATUSES.indexOf(status);

/**
 * Linha do tempo do pedido.
 *
 * O banco guarda a data de criacao, do envio e da aprovacao — as outras
 * etapas mostram so o que significam, sem inventar horario.
 */
const timelineOf = (order: StoredOrder) => {
  const at = rank(order.status);
  const step = (
    status: OrderStatus,
    title: string,
    done: string,
    next: string,
    date?: string | null,
  ) => {
    // Cancelado sai da sequência: nenhuma etapa conta como cumprida, só as
    // datas que de fato aconteceram continuam aparecendo.
    const reached = order.status !== "cancelado" && at >= rank(status);
    return {
      title,
      detail: date ? `${done} ${formatDateTime(date)}` : reached ? done : next,
      done: reached,
      current: order.status === status,
    };
  };
  return [
    step("salvo", "Montado e salvo", "Guardado na sua conta em", "", order.createdAt),
    step(
      "enviado",
      "Enviado para a equipe",
      order.sentAt ? "Enviado em" : "A equipe recebeu o pedido.",
      "Envie quando estiver tudo certo.",
      order.sentAt,
    ),
    step(
      "pendente_aprovacao",
      "Arte enviada para você",
      "A equipe mandou a arte no seu WhatsApp para você aprovar.",
      "A equipe confere o pedido e manda a arte no seu WhatsApp.",
    ),
    step(
      "aprovado",
      "Arte aprovada",
      order.approvedAt ? "Aprovada em" : "Você aprovou a arte.",
      "Depois que você aprovar pelo WhatsApp, a equipe registra aqui.",
      order.approvedAt,
    ),
    step(
      "em_producao",
      "Em produção",
      "Seu uniforme está sendo produzido.",
      "Começa logo depois da aprovação.",
    ),
    step("concluido", "Concluído", "Pedido pronto!", "A equipe avisa quando estiver pronto."),
  ];
};

function ClientePedido() {
  const { pedidoId } = Route.useParams();
  const navigate = useNavigate();
  const order = useMyOrder(pedidoId);
  const duplicate = useDuplicateMyOrder();
  const send = useSendMyOrder();
  const remove = useDeleteMyOrder();
  const storeSettings = useStoreSettings().data;
  const storePhone = storeSettings?.whatsappNumber;
  const storePayment = storeSettings?.payment;

  if (!order.data) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <AdminCard>
          <ListState
            loading={order.isLoading}
            error={order.error}
            empty={!order.isLoading}
            emptyLabel="Pedido não encontrado na sua conta."
          />
        </AdminCard>
      </div>
    );
  }

  const data = order.data;
  const pieces = data.items.reduce((sum, item) => sum + item.quantity, 0);
  const editable = isEditableStatus(data.status);
  const timeline = timelineOf(data);

  const failure = (fallback: string) => (cause: unknown) =>
    toast.error(cause instanceof Error ? cause.message : fallback);

  const handleSend = () =>
    send.mutate(data.id, {
      onSuccess: () => toast.success(`Pedido #${padOrderNumber(data.number)} enviado para a equipe!`),
      onError: failure("Não foi possível enviar o pedido."),
    });

  const handleDelete = () => {
    if (!window.confirm(`Excluir o pedido #${padOrderNumber(data.number)}?`)) return;
    remove.mutate(data.id, {
      onSuccess: () => {
        toast.success("Pedido excluído.");
        void navigate({ to: "/minha-conta/pedidos" });
      },
      onError: failure("Não foi possível excluir o pedido."),
    });
  };

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex items-center gap-3">
        <Link
          to="/minha-conta/pedidos"
          aria-label="Voltar para meus pedidos"
          className="grid h-9 w-9 place-items-center rounded-full border border-mb-line bg-white text-mb-navy transition-colors hover:border-mb-navy"
        >
          <ArrowLeft className="h-[18px] w-[18px]" />
        </Link>
        <Link
          to="/minha-conta/pedidos"
          className="text-sm font-semibold text-mb-muted hover:text-mb-navy"
        >
          Meus pedidos
        </Link>
        <ChevronRight className="h-4 w-4 text-mb-muted" />
        <span className="font-display text-lg font-bold tabular-nums text-mb-navy">
          #{padOrderNumber(data.number)}
        </span>
        <OrderPdfButton order={data} kind="pedido" label="Baixar PDF" className="ml-auto" />
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_380px]">
        {/* Coluna esquerda — o pedido */}
        <div className="flex flex-col gap-6">
          <AdminCard className="grid gap-6 p-4 sm:p-6 md:grid-cols-[320px_1fr]">
            <PreviewStage items={data.items} faceToggle className="h-70 rounded-mb-button" />

            <div>
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill status={data.status} />
                {data.payment && (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold leading-4 text-emerald-800">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Pagamento confirmado
                  </span>
                )}
                <span className="text-xs text-mb-muted">
                  Criado em {formatDateTime(data.createdAt)}
                </span>
              </div>

              <h1 className="mt-3 font-display text-3xl font-bold tabular-nums tracking-tight text-mb-navy sm:text-4xl">
                {formatBRL(data.total)}
              </h1>
              <p className="mt-1 text-sm text-mb-muted">
                {countLabel(pieces, "peça", "peças")} em{" "}
                {countLabel(data.items.length, "item configurado", "itens configurados")}
              </p>

              <p className="mt-6 text-xs font-bold uppercase tracking-[0.1em] text-mb-muted">
                Onde está seu pedido
              </p>
              {data.status === "cancelado" && (
                <p className="mt-3 rounded-mb-field bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive">
                  Este pedido foi cancelado. Fale com a equipe se precisar retomá-lo.
                </p>
              )}
              <div className="mt-3 flex flex-col gap-3">
                {timeline.map((step) => (
                  <div key={step.title} className="flex items-start gap-3">
                    <span
                      className={cn(
                        "mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full",
                        step.done
                          ? "bg-mb-navy text-white"
                          : "border border-dashed border-mb-line bg-white",
                        step.current && "ring-4 ring-mb-yellow/40",
                      )}
                    >
                      {step.done && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                    </span>
                    <div>
                      <p
                        className={cn(
                          "text-sm font-bold",
                          step.done ? "text-mb-ink" : "text-mb-muted",
                        )}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs leading-4 text-mb-muted text-pretty">{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AdminCard>

          <AdminCard>
            <div className="flex items-center justify-between border-b border-mb-line px-4 py-4 sm:px-6">
              <h2 className="font-display text-base font-bold text-mb-navy">Itens do pedido</h2>
              <span className="text-xs font-bold text-mb-muted">{countLabel(pieces, "peça", "peças")}</span>
            </div>

            {data.items.map((item, index) => (
              <div
                key={`${item.pieceId}-${index}`}
                className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-mb-line px-4 py-4 sm:flex-nowrap sm:px-6"
              >
                <ItemPreview item={item} />
                <div className="min-w-0 flex-1 basis-48">
                  <p className="text-sm font-bold text-mb-ink">{item.name}</p>
                  <p className="text-xs leading-4 text-mb-muted text-pretty">
                    {item.optionsSummary}
                  </p>
                  {item.units && (
                    <UnitGridView grid={item.units} className="mt-2.5 text-mb-ink" />
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold tabular-nums text-mb-navy">
                    {formatBRL(item.total)}
                  </p>
                  <p className="text-xs tabular-nums text-mb-muted">
                    {item.quantity} × {formatBRL(item.unitPrice)}
                  </p>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between px-4 py-4 sm:px-6">
              <span className="text-sm font-bold text-mb-ink">Valor total</span>
              <span className="font-display text-xl font-bold tabular-nums text-mb-navy">
                {formatBRL(data.total)}
              </span>
            </div>
          </AdminCard>
        </div>

        {/* Coluna direita — o que dá para fazer agora */}
        <div className="flex flex-col gap-6">
          {editable && (
            <AdminCard className="p-5 sm:p-6">
              <h2 className="font-display text-base font-bold text-mb-navy">
                Pronto para enviar?
              </h2>
              <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
                O pedido está guardado só com você. Ao enviar, a equipe recebe, confere e prepara a
                arte para você aprovar. Depois do envio, ele não muda mais por aqui.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={send.isPending}
                  className="flex w-full items-center justify-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3.5 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {send.isPending ? (
                    <Loader2 className="h-[18px] w-[18px] animate-spin" />
                  ) : (
                    <Send className="h-[18px] w-[18px]" />
                  )}
                  Enviar pedido
                </button>
                <Link
                  to="/"
                  search={{ pedido: data.id }}
                  className="flex w-full items-center justify-center gap-2 rounded-mb-button border border-mb-line px-5 py-3 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                >
                  <Pencil className="h-[18px] w-[18px]" />
                  Editar no configurador
                </Link>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={remove.isPending}
                  className="flex w-full items-center justify-center gap-2 rounded-mb-button px-5 py-2.5 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir pedido
                </button>
              </div>
            </AdminCard>
          )}

          {data.status === "pendente_aprovacao" && (
            <AdminCard className="border-orange-300 p-5 sm:p-6">
              <h2 className="font-display text-base font-bold text-mb-navy">
                A arte está no seu WhatsApp
              </h2>
              <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
                A equipe conferiu o pedido e mandou a arte para você. Responda por lá aprovando ou
                pedindo ajustes — assim que você aprovar, a equipe registra aqui e o pedido segue
                para a produção.
              </p>
              <a
                href={storeChatUrl(data, storePhone)}
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3.5 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5"
              >
                <MessageCircle className="h-[18px] w-[18px]" />
                Responder no WhatsApp
              </a>
            </AdminCard>
          )}

          {/* Pagamento pelo site: prévia até a integração com o Mercado Pago. */}
          {!editable && !data.payment && data.status !== "cancelado" && (
            <AdminCard className="p-5 sm:p-6">
              <h2 className="flex items-center gap-2 font-display text-base font-bold text-mb-navy">
                Pagamento
                <PreviewBadge />
              </h2>
              <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
                Em breve você paga por aqui, com segurança pelo Mercado Pago. Por enquanto, a equipe
                combina o pagamento com você no WhatsApp.
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {(storePayment?.methods ?? []).map((method) => (
                  <li
                    key={method}
                    className="rounded-full bg-mb-surface px-2.5 py-1 text-xs font-semibold text-mb-ink"
                  >
                    {PAYMENT_METHOD_LABEL[method]}
                    {method === "credit" && storePayment && storePayment.maxInstallments > 1
                      ? ` em até ${storePayment.maxInstallments}x`
                      : ""}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled
                className="mt-4 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-mb-button bg-[#009ee3] px-5 py-3 text-sm font-bold text-white opacity-60"
              >
                <Wallet className="h-[18px] w-[18px]" />
                Pagar com Mercado Pago — em breve
              </button>
            </AdminCard>
          )}

          {!editable && data.status !== "pendente_aprovacao" && (
            <AdminCard className="p-5 sm:p-6">
              <h2 className="font-display text-base font-bold text-mb-navy">
                Falar sobre este pedido
              </h2>
              <p className="mt-1 text-xs leading-4 text-mb-muted text-pretty">
                {data.status === "enviado"
                  ? "O pedido já está com a equipe. Se quiser, avise no WhatsApp que ele chegou."
                  : "Abre a conversa com a equipe já com o número do pedido."}
              </p>
              <a
                href={
                  data.status === "enviado"
                    ? storeNoticeUrl(data, storePhone)
                    : storeChatUrl(data, storePhone)
                }
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-mb-button border border-mb-line px-5 py-3 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
              >
                <MessageCircle className="h-[18px] w-[18px]" />
                {data.status === "enviado" ? "Avisar no WhatsApp" : "Chamar no WhatsApp"}
              </a>
            </AdminCard>
          )}

          <AdminCard className="p-5 sm:p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">Quer o mesmo de novo?</h2>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                disabled={duplicate.isPending}
                onClick={() =>
                  duplicate.mutate(data.id, {
                    onSuccess: (copy) =>
                      toast.success(
                        `Cópia criada como #${padOrderNumber(copy.number)} (rascunho).`,
                      ),
                    onError: failure("Não foi possível repetir o pedido."),
                  })
                }
                className="flex w-full items-center justify-center gap-2 rounded-mb-button border border-mb-line px-5 py-3 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-50"
              >
                {duplicate.isPending ? (
                  <Loader2 className="h-[18px] w-[18px] animate-spin" />
                ) : (
                  <Copy className="h-[18px] w-[18px]" />
                )}
                Repetir este pedido
              </button>
              {!editable && (
                <Link
                  to="/"
                  search={{ pedido: data.id }}
                  className="flex w-full items-center justify-center gap-2 rounded-mb-button border border-mb-line px-5 py-3 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                >
                  <Pencil className="h-[18px] w-[18px]" />
                  Abrir cópia no configurador
                </Link>
              )}
            </div>
            <p className="mt-2 text-[11px] leading-4 text-mb-muted">
              A cópia entra como rascunho novo — este pedido continua como está.
            </p>
          </AdminCard>

          <AdminCard className="p-5 sm:p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">Dados do pedido</h2>
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm text-mb-ink">
                <User className="h-4 w-4 shrink-0 text-mb-muted" />
                {data.customer.name}
              </div>
              <div className="flex items-center gap-2 text-sm text-mb-ink">
                <Phone className="h-4 w-4 shrink-0 text-mb-muted" />
                {data.customer.whatsapp}
              </div>
              {data.customer.email && (
                <div className="flex items-center gap-2 text-sm text-mb-ink">
                  <Mail className="h-4 w-4 shrink-0 text-mb-muted" />
                  <span className="truncate">{data.customer.email}</span>
                </div>
              )}
            </div>
            <Link
              to="/minha-conta/dados"
              className="mt-4 inline-block text-sm font-semibold text-mb-navy hover:text-mb-ink"
            >
              Alterar em Meus dados
            </Link>
          </AdminCard>
        </div>
      </div>
    </div>
  );
}
